import { Fragment, useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { EntityRef } from '../../components/EntityRef'
import { Icon } from '../../components/icons/Icon'
import { InlineAlert } from '../../components/InlineAlert'
import { StatusPill } from '../../components/StatusPill'
import { CollapsibleSection } from '../../components/CollapsibleSection'
import { WidgetErrorBoundary } from '../insights/widgets/WidgetErrorBoundary'
import { listIngestionPipelines, listSourceSystems } from '../../api/ingest'
import { getBackgroundJob, listBackgroundJobs, type DataJobKindFilter } from '../../api/jobs'
import { ApiError } from '../../api/problem'
import { label } from '../../lib/labels'
import type { IngestionPipelineView, SourceSystemView } from '../../types/ingest'
import {
  KIND_LABEL,
  STATUS_VARIANT,
  formatDateTime,
  statusLabel,
  type BackgroundJobDetailView,
  type BackgroundJobView,
} from '../../types/job'

function StatusCell({ status }: { status: string }) {
  return <StatusPill variant={STATUS_VARIANT[status] ?? 'neutral'} label={statusLabel(status)} />
}

const KIND_FILTERS: { value: DataJobKindFilter; label: string }[] = [
  { value: 'all', label: 'All activity' },
  { value: 'ingestion_run', label: 'Ingestion runs' },
  { value: 'bulk_update', label: 'Bulk update' },
  { value: 'bulk_delete', label: 'Bulk delete' },
  { value: 'bulk_purge', label: 'Bulk purge' },
]

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof ApiError ? (err.problem.detail ?? err.message) : fallback
}

/**
 * `/data/import` — replaces the M20/M23.6 `ComingSoonStub`. Three real, independently-fetching
 * sections (each its own `WidgetErrorBoundary`, AC26-style isolation): the import-source registry,
 * the ingestion-pipeline catalog, and a unified "Recent activity" read over EVERY async job this
 * platform submits (ingestion runs, bulk update/delete/purge, and beyond) — all three ride
 * already-published, real GETs (`SourceSystemController`/`IngestionPipelineController`/
 * `BackgroundJobController`); no schema change, no migration.
 */
export function DataImportScreen() {
  return (
    <div
      style={{
        padding: 'var(--iw-space-5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--iw-space-4)',
        height: '100%',
        overflow: 'auto',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-3)', flexWrap: 'wrap' }}>
        <span style={{ color: 'var(--iw-accent)', display: 'inline-flex' }}>
          <Icon name="database" size={22} />
        </span>
        <div style={{ flex: 1 }}>
          <h1 style={{ margin: 0, fontSize: 'var(--iw-size-xl)' }}>Data &amp; bulk ops</h1>
          <p style={{ margin: 0, color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-sm)' }}>
            Import sources, ingestion pipelines, and the platform-wide async job history (FR-M20.1–M20.10, FR-M23.6).
          </p>
        </div>
        <Link to="/data/bulk" className="iw-btn iw-btn--primary">
          <Icon name="rows" size={14} /> Go to Bulk operations
        </Link>
      </div>

      <WidgetErrorBoundary title="Import sources" icon="database">
        <CollapsibleSection title="Import sources">
          <SourceSystemsPanel />
        </CollapsibleSection>
      </WidgetErrorBoundary>

      <WidgetErrorBoundary title="Ingestion pipelines" icon="database">
        <CollapsibleSection title="Ingestion pipelines">
          <IngestionPipelinesPanel />
        </CollapsibleSection>
      </WidgetErrorBoundary>

      <WidgetErrorBoundary title="Recent activity" icon="activity">
        <RecentActivityPanel />
      </WidgetErrorBoundary>
    </div>
  )
}

function SourceSystemsPanel() {
  const [rows, setRows] = useState<SourceSystemView[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const fetchRows = useCallback(() => {
    setError(null)
    setRows(null)
    listSourceSystems({ limit: 100 })
      .then((page) => setRows(page.items))
      .catch((err) => setError(errorMessage(err, 'Could not load import sources.')))
  }, [])

  useEffect(() => {
    fetchRows()
  }, [fetchRows])

  if (error) {
    return (
      <InlineAlert
        severity="error"
        action={
          <button type="button" className="iw-btn iw-btn--sm" onClick={fetchRows}>
            Try again
          </button>
        }
      >
        {error}
      </InlineAlert>
    )
  }
  if (rows === null) {
    return <p style={{ color: 'var(--iw-text-muted)' }}>Loading import sources…</p>
  }
  if (rows.length === 0) {
    return (
      <div style={{ color: 'var(--iw-text-muted)', textAlign: 'center', padding: 'var(--iw-space-5)' }}>
        <Icon name="database" size={24} />
        <p>No import sources configured yet for this tenant.</p>
      </div>
    )
  }
  return (
    <table className="iw-table" aria-label="Import sources">
      <thead>
        <tr>
          <th scope="col">Name</th>
          <th scope="col">Kind</th>
          <th scope="col">Region</th>
          <th scope="col">Status</th>
          <th scope="col">Updated</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((s) => (
          <tr key={s.id}>
            <td>{s.name}</td>
            <td>{s.kind}</td>
            <td>{s.region}</td>
            <td>
              <StatusCell status={s.status} />
            </td>
            <td style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>
              {formatDateTime(s.updatedAt)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function IngestionPipelinesPanel() {
  const [rows, setRows] = useState<IngestionPipelineView[] | null>(null)
  const [sourceNames, setSourceNames] = useState<Map<string, string>>(new Map())
  const [error, setError] = useState<string | null>(null)

  const fetchRows = useCallback(() => {
    setError(null)
    setRows(null)
    Promise.all([listIngestionPipelines({ limit: 100 }), listSourceSystems({ limit: 100 })])
      .then(([pipelines, sources]) => {
        setRows(pipelines.items)
        setSourceNames(new Map(sources.items.map((s) => [s.id, s.name])))
      })
      .catch((err) => setError(errorMessage(err, 'Could not load ingestion pipelines.')))
  }, [])

  useEffect(() => {
    fetchRows()
  }, [fetchRows])

  if (error) {
    return (
      <InlineAlert
        severity="error"
        action={
          <button type="button" className="iw-btn iw-btn--sm" onClick={fetchRows}>
            Try again
          </button>
        }
      >
        {error}
      </InlineAlert>
    )
  }
  if (rows === null) {
    return <p style={{ color: 'var(--iw-text-muted)' }}>Loading ingestion pipelines…</p>
  }
  if (rows.length === 0) {
    return (
      <div style={{ color: 'var(--iw-text-muted)', textAlign: 'center', padding: 'var(--iw-space-5)' }}>
        <Icon name="database" size={24} />
        <p>No ingestion pipelines configured yet for this tenant.</p>
      </div>
    )
  }
  return (
    <table className="iw-table" aria-label="Ingestion pipelines">
      <thead>
        <tr>
          <th scope="col">Source</th>
          <th scope="col">Target entity</th>
          <th scope="col">Load mode</th>
          <th scope="col">Schedule</th>
          <th scope="col">Status</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((p) => (
          <tr key={p.id}>
            <td>
              <EntityRef kind="record" id={p.sourceSystemId} name={sourceNames.get(p.sourceSystemId)} />
            </td>
            <td>{label('entity', p.targetEntity)}</td>
            <td>{label('loadMode', p.loadMode)}</td>
            <td>{p.schedule ?? '—'}</td>
            <td>
              <StatusCell status={p.status} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function RecentActivityPanel() {
  const [kind, setKind] = useState<DataJobKindFilter>('all')
  const [rows, setRows] = useState<BackgroundJobView[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [detail, setDetail] = useState<BackgroundJobDetailView | null>(null)
  const [detailError, setDetailError] = useState<string | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)

  const fetchRows = useCallback(() => {
    setError(null)
    setRows(null)
    setExpandedId(null)
    listBackgroundJobs({ kind: kind === 'all' ? null : kind, limit: 50 })
      .then((page) => setRows(page.items))
      .catch((err) => setError(errorMessage(err, 'Could not load recent activity.')))
  }, [kind])

  useEffect(() => {
    fetchRows()
  }, [fetchRows])

  function toggleRow(id: string) {
    if (expandedId === id) {
      setExpandedId(null)
      return
    }
    setExpandedId(id)
    setDetail(null)
    setDetailError(null)
    setDetailLoading(true)
    getBackgroundJob(id)
      .then((d) => setDetail(d))
      .catch((err) => setDetailError(errorMessage(err, 'Could not load this job’s detail.')))
      .finally(() => setDetailLoading(false))
  }

  return (
    <section
      style={{
        background: 'var(--iw-surface)',
        border: '1px solid var(--iw-border)',
        borderRadius: 'var(--iw-radius-lg)',
        padding: 'var(--iw-space-4)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--iw-space-3)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-2)', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 'var(--iw-size-md)' }}>Recent activity</h2>
        <span style={{ flex: 1 }} />
        <div role="group" aria-label="Job kind filter" style={{ display: 'flex', gap: 'var(--iw-space-2)', flexWrap: 'wrap' }}>
          {KIND_FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              className="iw-chip"
              aria-pressed={kind === f.value}
              onClick={() => setKind(f.value)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {error ? (
        <InlineAlert
          severity="error"
          action={
            <button type="button" className="iw-btn iw-btn--sm" onClick={fetchRows}>
              Try again
            </button>
          }
        >
          {error}
        </InlineAlert>
      ) : rows === null ? (
        <p style={{ color: 'var(--iw-text-muted)' }}>Loading recent activity…</p>
      ) : rows.length === 0 ? (
        <div style={{ color: 'var(--iw-text-muted)', textAlign: 'center', padding: 'var(--iw-space-5)' }}>
          <Icon name="inbox" size={24} />
          <p>
            {kind === 'all'
              ? 'No import or bulk-operation activity yet in this tenant.'
              : `No ${KIND_FILTERS.find((f) => f.value === kind)?.label.toLowerCase()} yet in this tenant.`}
          </p>
        </div>
      ) : (
        <table className="iw-table" aria-label="Recent activity">
          <thead>
            <tr>
              <th scope="col">Kind</th>
              <th scope="col">Status</th>
              <th scope="col">Phase</th>
              <th scope="col">Counts</th>
              <th scope="col">Started</th>
              <th scope="col">Completed</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((job) => (
              <Fragment key={job.id}>
                <tr
                  onClick={() => toggleRow(job.id)}
                  style={{ cursor: 'pointer' }}
                  aria-expanded={expandedId === job.id}
                >
                  <td>{KIND_LABEL[job.kind] ?? job.kind}</td>
                  <td>
                    <StatusCell status={job.status} />
                  </td>
                  <td style={{ color: 'var(--iw-text-muted)' }}>{job.phase ?? '—'}</td>
                  <td style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>
                    {job.counts.succeeded} ok · {job.counts.failed} failed · {job.counts.skipped} skipped (of{' '}
                    {job.counts.total})
                  </td>
                  <td style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>
                    {formatDateTime(job.startedAt)}
                  </td>
                  <td style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>
                    {formatDateTime(job.completedAt)}
                  </td>
                </tr>
                {expandedId === job.id ? (
                  <tr>
                    <td colSpan={6} style={{ background: 'var(--iw-neutral-100)' }}>
                      {detailLoading ? (
                        <p style={{ color: 'var(--iw-text-muted)', margin: 0 }}>Loading detail…</p>
                      ) : detailError ? (
                        <InlineAlert severity="error">{detailError}</InlineAlert>
                      ) : detail ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-1)' }}>
                          <span>
                            Source: <code>{detail.job.sourceType}</code> / <code>{detail.job.sourceId}</code>
                          </span>
                          <span>Region: {detail.job.region}</span>
                          {detail.job.errorReportRef ? (
                            <span style={{ color: 'var(--iw-text-muted)' }}>An error report is available for this job.</span>
                          ) : null}
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}
